import React from 'react';
import { Button } from '@superops/ui-design-system';
import { useIntl, T2, getTranslatedText } from '@nile/components';
import styled from 'styled-components';
import { planDescriptionKeyMap } from '@nile/banyan/src/Utils/pricingHelper';
import { FormattedMessage } from 'react-intl';

const PlanCardWrapper = styled.div`
  width: 24%;
  .wrap {
    height: calc(100% - 40px);
  }
  .tag {
    width: 145px;
    height: 27px;
    background: #ff1462;
    border-radius: 5px;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    color: #fff;
    padding-top: 5px;
    top: 16px;
    position: relative;
    z-index: 3;
    &.no-color {
      background: none;
    }
  }
  .popular {
    border: 2px solid #f05;
  }
  .pricing-plans-content-wrap {
    min-height: 206px;
  }
  .name {
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #ff1462;
    margin-bottom: 6px;
  }
  .excerpt {
    font-family: Poppins, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 30px;
    color: #4d4d4d;
    margin-bottom: 20px;
  }
  .pricing-info-wrap {
    background: #f7f9fa;
    border-radius: 5px;
    min-height: 60px;
    height: auto;
    padding: 10px 20px;
    font-size: 12px;
    line-height: 20px;
    color: #666;
    margin-bottom: 40px;
  }
  .price {
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 56px;
    line-height: 64px;
    color: #170426;
    margin-bottom: 8px;
  }
  .details {
    font-family: Poppins, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #4d4d4d;
    margin-bottom: 42px;
  }
  .points {
    font-family: Poppins, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    color: #170426;
    position: relative;
    height: 18px;
    .sp-o span {
      loat: right;
      color: #4d4d4d;
      font-weight: 400;
    }
  }
  .line {
    width: 88px;
    height: 2px;
    border-top: 2px solid #f05;
    margin: 24px 0;
  }
  .feature-parent {
    margin-top: 48px;
    margin-bottom: 24px;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 30px;
    color: #170426;
  }
  .feature {
    margin-bottom: 8px;
    font-family: Poppins, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #4d4d4d;
    position: relative;
  }
  .content {
    background: #fff;
    box-shadow: 0 20px 40px -8px rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    min-height: auto;
    height: auto;
    padding: 32px 24px;
    height: 100%;
    position: relative;
    z-index: 2;
  }
  .btn-holder {
    text-align: center;
    .Polaris-Button {
      transition: 0.5s;
      &:hover {
        background: linear-gradient(to bottom, #0e0f16, #06070a);
        color: white;
      }
    }
  }
`;

const PlanCard = (props) => {
  const {
    data,
    tenure,
    setSelectedPlan,
    disablePurchase,
    locale,
    currency
  } = props;

  const intl = useIntl();

  const planFeatures =
    [...new Set(data?.planFeatures?.map((item) => item.module))] || [];

  const planPriceValue = data?.planPrices?.filter(
    (prPlan) =>
      prPlan?.currency?.currencyCode === currency &&
      prPlan?.duration === tenure &&
      prPlan?.durationValue === 1
  )?.[0]?.priceValue;

  return (
    <PlanCardWrapper>
      <div className="wrap col-lg-3 col-md-6">
        <center>
          <div className={data?.planId === '3' ? 'tag' : `tag no-color`}>
            {data?.planId === '3' && 'MOST POPULAR'}
          </div>
        </center>
        <div className={data?.planId === '3' ? `content popular` : 'content'}>
          <div className="pr-pl-dsk">
            <p className="name">{data?.planName}</p>
            <p className="excerpt">
              <T2>
                {data?.chargeBeePlanId
                  ? getTranslatedText(
                      intl,
                      planDescriptionKeyMap[data?.chargeBeePlanId]
                    )
                  : ''}
              </T2>
            </p>
            <div className="pricing-info-wrap">
              <FormattedMessage
                id={`app.pricing.${data?.chargeBeePlanId || 'solo'}.planFor`}
              />
            </div>
            <div className="pl-slide-wrap">
              <p className="price">
                {locale &&
                  currency &&
                  tenure &&
                  new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency,
                    minimumFractionDigits: 0,
                    currencyDisplay: 'narrowSymbol'
                  }).format(
                    (tenure === 'YEARLY'
                      ? planPriceValue / 12
                      : planPriceValue) / 100
                  )}
              </p>
            </div>
            <p className="details">
              <T2>
                {data?.chargeBeePlanId === 'solo' ? (
                  <FormattedMessage id="app.pricing.solo.billingcycle" />
                ) : tenure === 'YEARLY' ? (
                  <FormattedMessage id="app.pricing.annual.billingcycle" />
                ) : (
                  <FormattedMessage id="app.pricing.monthly.billingcycle" />
                )}
              </T2>
            </p>
            <p className="points">
              {data?.endPointsCount > 0 && (
                <div className="sp-o">
                  <FormattedMessage id="app.pricing.endpoints" />:{' '}
                  <span>{data?.endPointsCount}/tech </span>{' '}
                </div>
              )}
            </p>
            <div className="sp-o">
              <div className="line"> </div>
            </div>
            <div className="btn-holder">
              <Button
                primary={data?.planId === '3'}
                fullWidth
                onClick={() => setSelectedPlan(data)}
                disabled={disablePurchase}
              >
                <FormattedMessage id="app.pricing.purchase.button" />
              </Button>
            </div>
          </div>
          <div className="pr-pl-dsk">
            {planFeatures.map((feature) => {
              return (
                <div className="pricing-plans-content-wrap">
                  <p className="feature-parent">{feature}</p>
                  {data?.planFeatures
                    ?.filter((subfeat) => subfeat.module === feature)
                    ?.map((subfeat) => (
                      <p className="feature">
                        <T2>{subfeat.featureName}</T2>
                      </p>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PlanCardWrapper>
  );
};

export default PlanCard;
